/**
 * i18n bootstrap (KNOWN_ISSUES §1.48).
 *
 * Imported once from `index.js` BEFORE the React render so every
 * subsequent `useTranslation()` call has the resource bundles ready.
 *
 * Behaviour:
 *   • Default language: `en`.
 *   • Language detection order: localStorage (key `mm.lang`) →
 *     browser `navigator.language` → fallback `en`. Detection
 *     covers a fresh visitor; the Settings-page language switcher
 *     is the explicit override + persists to localStorage.
 *   • Resources are bundled into the JS at build time
 *     (`require("./locales/...")`). For a larger locale set we'd
 *     swap to `i18next-http-backend` to lazy-load per-locale JSON;
 *     bundling is fine while we have ≤3 languages.
 *   • Interpolation: i18next's default `{{var}}` syntax. React
 *     escapes string values automatically so XSS isn't a concern.
 *   • Plural forms: i18next's `_one` / `_other` suffix convention.
 *
 * Translation-key convention: `<page>.<element>.<purpose>`.
 *   Examples:
 *     • login.title
 *     • login.button.signIn
 *     • errors.network.timeout
 *     • common.cancel
 *
 * Adding a new locale: drop a JSON file alongside `en.json` and
 * register it in `resources` below. See `i18n/README.md`.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import en from "./locales/en.json";
import es from "./locales/es.json";


export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English", nativeName: "English" },
  { code: "es", label: "Spanish", nativeName: "Español" },
];

export const STORAGE_KEY = "mm.lang";


i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      es: { translation: es },
    },
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    nonExplicitSupportedLngs: true,  // `es-MX` falls back to `es`
    interpolation: {
      escapeValue: false,  // React already escapes
    },
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: STORAGE_KEY,
      caches: ["localStorage"],
    },
    react: {
      useSuspense: false,  // resources are bundled — no suspense needed
    },
  });


export default i18n;
